import { type BaseTooltipProps } from "@common/controls/Tooltip/Base/component";
import { type PositionType } from "@common/controls/Tooltip/lib";
import { type GenericMap, type WithChildren } from "@common/declarations";
import { removeItem } from "@common/lib/array";
import { identity } from "lodash";
import React from "react";
import Component from "./Component";
import { getShowingTooltipInfo, getTooltipInfo } from "./lib";

export type TooltipContextShowArgs = {
  hideAfter: number;
  target: HTMLElement;
  tooltip: string;
};

export type TooltipContextProps = {
  show: (args: TooltipContextShowArgs) => void;
};

export const TooltipContext = React.createContext<TooltipContextProps>({
  show: identity
});

export type ShowingTooltipInfo = {
  props: BaseTooltipProps;
  target: HTMLElement;
};
export type TooltipInfo = {
  props: BaseTooltipProps | null;
  target: HTMLElement;
};
export const TooltipContextProvider = (props: WithChildren) => {
  const [showTooltips, setShowTooltips] = React.useState<ShowingTooltipInfo[]>([]);
  const [tooltip, setTooltip] = React.useState<TooltipInfo | null>(null);

  React.useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      window.requestAnimationFrame(() => {
        let target = e.target as HTMLElement;
        if (target && !target.dataset.tooltip) target = target.closest("[data-tooltip]")!;

        if (target && target.dataset.tooltip && tooltip?.target !== target) {
          setTooltip(getTooltipInfo(target));
        } else if (tooltip && !target) {
          setTooltip(null);
        }
      });
    };

    document.addEventListener("mousemove", onMouseMove);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
    };
  }, [tooltip]);

  React.useEffect(() => {
    if (!tooltip?.target) return;

    const observer = new MutationObserver(() => {
      if (!document.contains(tooltip?.target)) {
        observer.disconnect();

        setTooltip(null);
      }
    });

    observer.observe(document, {
      childList: true,
      subtree: true
    });

    return () => {
      observer && observer.disconnect();
    };
  }, [tooltip?.target]);

  React.useEffect(() => {
    if (!tooltip?.target) return;

    const observer = new MutationObserver((mutations) => {
      const wasRemoved = mutations.some((x) => x.attributeName === "data-tooltip" && !tooltip.target.dataset.tooltip);
      if (wasRemoved) {
        setTooltip((x) => ({ target: x!.target, props: null }));
      } else {
        const wasAdded = mutations.some((x) => x.attributeName === "data-tooltip" && tooltip.target.dataset.tooltip);
        if (wasAdded) {
          setTooltip(getTooltipInfo(tooltip!.target));
        }
      }
    });

    observer.observe(tooltip.target, {
      attributes: true // configure it to listen to attribute changes
    });

    return () => {
      observer && observer.disconnect();
    };
  }, [tooltip?.target]);

  React.useEffect(() => {
    if (!tooltip?.props) return;

    const interval = setInterval(() => {
      if (!tooltip!.target.matches(":hover")) {
        setTooltip(null);
      }
    }, 30);

    return () => {
      interval && clearInterval(interval);
    };
  }, [tooltip]);

  const value = React.useMemo(
    (): TooltipContextProps => ({
      show: (args: TooltipContextShowArgs) => {
        const tooltipInfo = getShowingTooltipInfo(args);

        setShowTooltips((x) => [...x, tooltipInfo]);

        setTimeout(() => setShowTooltips((x) => removeItem(x, (y) => y === tooltipInfo)), args.hideAfter);
      }
    }),
    []
  );

  return (
    <TooltipContext.Provider value={value}>
      {props.children}

      {showTooltips.map((x, i) => (
        <Component key={i} target={x.target} base={x.props} />
      ))}
      {tooltip?.props && <Component target={tooltip.target} base={tooltip.props} />}
    </TooltipContext.Provider>
  );
};

type GetTooltipAttributesOptions = {
  delay?: number;
  position?: PositionType;
};
export const getTooltipAttributes = (tooltip: string | null | undefined, options?: GetTooltipAttributesOptions) => {
  if (!tooltip) return {};

  const res: GenericMap<any> = {
    "data-tooltip": tooltip
  };

  res["data-tooltip-delay"] = options?.delay ?? 100;

  return res;
};
